import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, SkipForward, SkipBack, X, Heart, Share2, Clock, List, Sparkles } from 'lucide-react';
import { AudioVisualBadge } from '@/components/audio/AudioVisuals';
import { comboApi, getAudioFileUrl } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const ComboPlayer = ({ combo, onClose, onUpdate }) => {
  const { user } = useAuth();
  const [audios, setAudios] = useState(combo.audios || []);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [pauseCountdown, setPauseCountdown] = useState(0);
  const [isSaved, setIsSaved] = useState(combo.is_saved);
  const [loadingAudios, setLoadingAudios] = useState(false);
  const [isAutoFilled, setIsAutoFilled] = useState(false);
  const audioRef = useRef(null);
  const pauseTimerRef = useRef(null);

  // Cargar audios automáticamente si el combo está vacío
  useEffect(() => {
    const loadAutoAudios = async () => {
      if ((!combo.audios || audios.length === 0) && !loadingAudios) {
        setLoadingAudios(true);
        try {
          const autoAudios = await comboApi.getAutoAudios(combo.combo_id, 10);
          if (autoAudios && autoAudios.length > 0) {
            setAudios(autoAudios);
            setIsAutoFilled(true);
          }
        } catch (error) {
          console.error('Error loading auto audios:', error);
        } finally {
          setLoadingAudios(false);
        }
      }
    };
    loadAutoAudios();
  }, [combo.combo_id, combo.audios, loadingAudios]);

  const currentAudio = audios[currentIndex];
  const pauseDuration = combo.pause_between_seconds || 2;

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Start pause between audios
  const startPause = useCallback(() => {
    setIsPaused(true);
    setPauseCountdown(pauseDuration);
    
    let count = pauseDuration;
    pauseTimerRef.current = setInterval(() => {
      count--;
      setPauseCountdown(count);
      if (count <= 0) {
        clearInterval(pauseTimerRef.current);
        setIsPaused(false);
        // Move to next audio
        if (currentIndex < audios.length - 1) {
          setCurrentIndex(prev => prev + 1);
        } else {
          // Combo finished
          setIsPlaying(false);
          setCurrentIndex(0);
        }
      }
    }, 1000);
  }, [currentIndex, audios.length, pauseDuration]);

  // Handle audio end
  const handleAudioEnd = useCallback(() => {
    if (currentIndex < audios.length - 1) {
      startPause();
    } else {
      // Combo finished
      setIsPlaying(false);
      setCurrentIndex(0);
    }
  }, [currentIndex, audios.length, startPause]);

  // Play/Pause toggle
  const togglePlay = async () => {
    if (!audioRef.current) return;

    if (isPaused) {
      // Skip pause
      clearInterval(pauseTimerRef.current);
      setIsPaused(false);
      if (currentIndex < audios.length - 1) {
        setCurrentIndex(prev => prev + 1);
      }
      return;
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      try {
        await audioRef.current.play();
        setIsPlaying(true);
        // Track play on first audio
        if (currentIndex === 0) {
          await comboApi.play(combo.combo_id);
        }
      } catch (error) {
        console.error('Play error:', error);
      }
    }
  };

  // Skip to next audio
  const skipNext = () => {
    if (isPaused) {
      clearInterval(pauseTimerRef.current);
      setIsPaused(false);
    }
    if (currentIndex < audios.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  // Skip to previous audio
  const skipPrev = () => {
    if (isPaused) {
      clearInterval(pauseTimerRef.current);
      setIsPaused(false);
    }
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    } else if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  };

  // Handle time update
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const prog = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setProgress(prog);
    }
  };

  // Auto-play when index changes
  useEffect(() => {
    if (audioRef.current && isPlaying && !isPaused) {
      audioRef.current.play().catch(console.error);
    }
  }, [currentIndex, isPlaying, isPaused]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (pauseTimerRef.current) {
        clearInterval(pauseTimerRef.current);
      }
    };
  }, []);

  // Toggle save
  const handleSave = async () => {
    if (!user) return;
    try {
      const result = await comboApi.toggleSave(combo.combo_id);
      setIsSaved(result.is_saved);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Save error:', error);
    }
  };

  // Share
  const handleShare = async () => {
    const slug = combo.slug || combo.combo_id;
    const shareUrl = `${window.location.origin}/c/${slug}`;
    const duration = formatTime(combo.total_duration_seconds || 0);
    
    try {
      await comboApi.share(combo.combo_id);
      
      if (navigator.share) {
        await navigator.share({
          text: `Me he acordado de ti con esto 🤗😅 ${shareUrl}`
        });
      } else {
        await navigator.clipboard.writeText(`Me he acordado de ti con esto 🤗😅 ${shareUrl}`);
        alert('Enlace copiado al portapapeles');
      }
    } catch (error) {
      console.error('Share error:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="combo-player">
      <div className="w-full max-w-lg neumorphic-card p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-800">{combo.name}</h2>
            <p className="text-sm text-gray-500">
              {audios.length} audios • {formatTime(combo.total_duration_seconds || audios.reduce((acc, a) => acc + (a.duration_seconds || 0), 0))}
            </p>
            {isAutoFilled && (
              <p className="text-xs text-purple-500 flex items-center gap-1 mt-1">
                <Sparkles size={12} /> Audios de usuarios que sigues
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="neumorphic-button-small p-2"
            data-testid="close-combo-player"
          >
            <X size={20} />
          </button>
        </div>

        {/* Loading state */}
        {loadingAudios && (
          <div className="text-center py-8">
            <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-2" />
            <p className="text-gray-500">Cargando audios...</p>
          </div>
        )}

        {/* No audios */}
        {!loadingAudios && audios.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">No hay audios disponibles para este combo</p>
            <p className="text-sm text-gray-400 mt-2">Sigue a más usuarios para llenar tus combos</p>
          </div>
        )}

        {/* Current Audio Display */}
        {!loadingAudios && currentAudio && (
          <div className="neumorphic-inset p-4 rounded-xl mb-4">
            {isPaused ? (
              <div className="text-center py-4">
                <p className="text-gray-500 mb-2">Pausa entre audios</p>
                <p className="text-4xl font-bold text-audioyou-accent">{pauseCountdown}</p>
                <p className="text-sm text-gray-400 mt-2">Toca para saltar</p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-4 mb-4">
                  <AudioVisualBadge
                    shape={currentAudio.shape}
                    color={currentAudio.color}
                    emoji={currentAudio.emoji}
                    language={currentAudio.language}
                    size="md"
                  />
                  <div className="flex-1">
                    <p className="text-sm text-gray-500">
                      Audio {currentIndex + 1} de {audios.length}
                    </p>
                    <p className="font-medium text-gray-800">@{currentAudio.username}</p>
                  </div>
                </div>

                {/* Hidden audio element */}
                <audio
                  ref={audioRef}
                  src={getAudioFileUrl(currentAudio.audio_url)}
                  onTimeUpdate={handleTimeUpdate}
                  onEnded={handleAudioEnd}
                  preload="auto"
                />

                {/* Progress bar */}
                <div className="neumorphic-progress h-2 rounded-full overflow-hidden mb-2">
                  <div 
                    className="h-full bg-audioyou-accent transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  <span>{audioRef.current ? formatTime(audioRef.current.currentTime) : '0:00'}</span>
                  <span>{formatTime(currentAudio.duration_seconds)}</span>
                </div>
              </>
            )}
          </div>
        )}

        {/* Controls */}
        <div className="flex items-center justify-center gap-4 mb-6">
          <button
            onClick={skipPrev}
            className="neumorphic-button-small p-3"
            disabled={currentIndex === 0 && !isPaused}
            data-testid="skip-prev"
          >
            <SkipBack size={20} />
          </button>
          
          <button
            onClick={togglePlay}
            className="neumorphic-button-accent w-16 h-16 rounded-full flex items-center justify-center"
            data-testid="play-pause-combo"
          >
            {isPlaying && !isPaused ? (
              <Pause size={28} />
            ) : (
              <Play size={28} className="ml-1" />
            )}
          </button>
          
          <button
            onClick={skipNext}
            className="neumorphic-button-small p-3"
            disabled={currentIndex === audios.length - 1 && !isPaused}
            data-testid="skip-next"
          >
            <SkipForward size={20} />
          </button>
        </div>

        {/* Audio list */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <List size={16} className="text-gray-400" />
            <span className="text-sm text-gray-500">Lista de audios</span>
          </div>
          <div className="max-h-40 overflow-y-auto space-y-2">
            {audios.map((audio, index) => (
              <button
                key={audio.audio_id}
                onClick={() => {
                  if (isPaused) {
                    clearInterval(pauseTimerRef.current);
                    setIsPaused(false);
                  }
                  // Si es el mismo audio, toggle play/pause
                  if (index === currentIndex) {
                    togglePlay();
                  } else {
                    setCurrentIndex(index);
                    setIsPlaying(true);
                  }
                }}
                className={`w-full p-2 rounded-lg flex items-center gap-3 transition-all ${
                  index === currentIndex 
                    ? 'neumorphic-inset bg-audioyou-accent/10' 
                    : 'hover:bg-gray-50'
                }`}
              >
                <span className="text-xs text-gray-400 w-4">{index + 1}</span>
                <AudioVisualBadge
                  shape={audio.shape}
                  color={audio.color}
                  emoji={audio.emoji}
                  language={audio.language}
                  size="sm"
                />
                <span className="text-sm text-gray-600 flex-1 text-left truncate">
                  @{audio.username}
                </span>
                <span className="text-xs text-gray-400">
                  {formatTime(audio.duration_seconds)}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-center gap-4 pt-4 border-t border-gray-100">
          <button
            onClick={handleSave}
            disabled={!user}
            className="neumorphic-button-small flex items-center gap-2 px-4 py-2"
            data-testid="save-combo"
          >
            <Heart 
              size={18} 
              className={isSaved ? 'fill-red-500 text-red-500' : 'text-gray-400'}
            />
            <span className="text-sm">Guardar</span>
          </button>
          
          <button
            onClick={handleShare}
            className="neumorphic-button-small flex items-center gap-2 px-4 py-2"
            data-testid="share-combo"
          >
            <Share2 size={18} className="text-blue-500" />
            <span className="text-sm">Compartir</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ComboPlayer;
